/**
 * Purpose
 * -------
 *
 * Page header sections will sometimes have title text that extends
 * over the page header bounds. In some cases, the title text can
 * cause overlapping issues with elements in the next section.
 *
 * This module aims to fix that by inserting a "page-header-spacer"
 * element into the top of following section, but only if the following
 * section has .section-margin or .section-padding
 *
 * The spacers are dynamically resized whenever the window is resized
 * since the page header title changes size and position depending on
 * a bunch of obscure media queries in the css. This was the easiest
 * solution I could think of to solve this problem.
 *
 */

import $ from 'jquery'
import debounce from 'lodash/debounce'

export function initialise() {
  const spacers = createAllSpacers()
  const update = function () {
    updateAllSpacerHeights(spacers)
  }

  // update every 200ms for the first 0.8 second, starting immediately
  for (let i = 0; i <= 5; i++) {
    setTimeout(update, i * 200)
  }

  const onResize = debounce(update, 100)

  $(window).on('resize', onResize)
}

function createAllSpacers() {
  const spacers = []
  $('section.page-header').each(function () {
    const $pageHeaderSection = $(this)
    const $pageHeaderBody = $pageHeaderSection.find('.page-header-body')
    const $nextSection = $pageHeaderSection.nextAll('section').first()
    if (nextSectionNeedsSpacer($nextSection)) {
      const $spacer = createPageHeaderSpacer($nextSection)
      spacers.push({ $spacer, $pageHeaderBody })
    }
  })
  return spacers
}

function nextSectionNeedsSpacer($nextSection) {
  return (
    $nextSection.is('.section-margin') ||
    $nextSection.is('.section-padding') ||
    $nextSection.is('[data-require-page-header-spacer]')
  )
}

function updateAllSpacerHeights(spacers) {
  spacers.map(({ $spacer, $pageHeaderBody }) => {
    applySpacerHeight($spacer, $pageHeaderBody)
  })
}

function createPageHeaderSpacer($nextSection) {
  const $spacer = $('<div data-page-header-spacer></div>')
  $nextSection.prepend($spacer)
  return $spacer
}

function applySpacerHeight($spacer, $pageHeaderBody) {
  const height = calculateSpacerHeight(getMeasurements($spacer, $pageHeaderBody))
  $spacer.css({ height })
}

function getMeasurements($spacer, $pageHeaderBody) {
  const spacerTop = $spacer.offset().top
  const pageHeaderBodyBottom = $pageHeaderBody.offset().top + $pageHeaderBody.height()
  const $spacerSection = $spacer.closest('section')
  const spacerSectionMarginTop = parseInt($spacerSection.css('marginTop'), 10)
  const spacerSectionPaddingTop = parseInt($spacerSection.css('paddingTop'), 10)
  const spacerSectionTopBuffer = spacerSectionMarginTop + spacerSectionPaddingTop
  const overlap = pageHeaderBodyBottom - spacerTop

  return {
    overlap,
    spacerSectionTopBuffer,
  }
}

function calculateSpacerHeight({ overlap, spacerSectionTopBuffer }) {
  if (overlap < 0) {
    return 0
  }
  if (spacerSectionTopBuffer > overlap) {
    return spacerSectionTopBuffer
  }
  return overlap
}
